'use client'

import { useApplySuggestedFilters, useFetchAISuggestions } from '@/src/hooks/srp/aiRecommendation'
import { useSortManager } from '@/src/hooks/srp/sortManager'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { setSRPFloatingAIChatEnabled } from '@/src/redux/slices/aiFeatureSlice'
import { resetAllAIState, setLastAppliedRecommendation, setLastSearchedInput, setTextInput } from '@/src/redux/slices/aiRecommendationSlice'
import { setOnwardSelectedSort, setReturnSelectedSort } from '@/src/redux/slices/srpStatesSlice'
import { useAppDispatch } from '@/src/redux/store'
import type { AIRecommendationSuggestionsResponse } from '@/src/types/aiRecommendation'
import { AI_ACTION, AI_EVENT_NAME } from '@/src/utils/analytics/ai/aiEvents'
import { useCallback, useEffect, useRef } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import { useAIFeatureEnableManager } from '../../AIFeatureEnableManager'
import { useGetSearchParamsObj } from '../searchParams'

/**
 * SRP AI filter: fetch suggestions, apply filters to results, then switch sort to Smart for the active leg.
 */
export function useSRPAIFilterManager (autoSearchWithQueryParam = false) {

    const { srpSmartFilterSort } = useAIFeatureEnableManager()
    const dispatch = useAppDispatch()
    const { query, srpResultsError, srpResultsLoading, invalidUrl, selectedJourneyLeg } =
        useSearchResults()
    const { fetchSuggestions } = useFetchAISuggestions()
    const { applySuggestedFilters } = useApplySuggestedFilters()
    const { sortOptions } = useSortManager()
    const { urlQueryObj } = useGetSearchParamsObj()
    const { triggerSrpAIEvent } = useClevertapForSrp()
    const aiPageLoadSentRef = useRef(false)
    const aiFilterQuery = urlQueryObj.ai_filter_query
    const searchWithAI = urlQueryObj.search_with_ai
    const smartSortOption = sortOptions.find(option => option.sortKey === 'smart')
    const smartSortDisplayValue = smartSortOption?.label || 'Smart'

    const applySmartSortForCurrentLeg = useCallback(() => {
        const nextSort = {
            sortKey: 'smart' as const,
            displayValue: smartSortDisplayValue
        }
        if (selectedJourneyLeg === 0) {
            dispatch(setOnwardSelectedSort(nextSort))
        } else {
            dispatch(setReturnSelectedSort(nextSort))
        }
    }, [dispatch, selectedJourneyLeg, smartSortDisplayValue])

    const applySmartAiFilters = useCallback(
        async (naturalLanguageInput: string) => {
            const payload = await fetchSuggestions(naturalLanguageInput)
            if (!payload) return
            applySuggestedFilters(payload)
            applySmartSortForCurrentLeg()
            // Store the applied preferences/sort from the home→SRP handoff too, so the first
            // chat follow-up can round-trip them instead of dropping the time range / sort.
            dispatch(setLastAppliedRecommendation({
                leg: selectedJourneyLeg,
                preferences: payload.preferences ?? null,
                sortPreference: payload.sortPreference ?? null
            }))
        },
        [applySmartSortForCurrentLeg, applySuggestedFilters, fetchSuggestions, dispatch, selectedJourneyLeg]
    )

    const applySmartAiPayload = useCallback(
        (payload: AIRecommendationSuggestionsResponse, naturalLanguageInput: string) => {
            applySuggestedFilters(payload)
            applySmartSortForCurrentLeg()
            // Persist the applied preferences/sort so the next turn can round-trip them to the AI
            // (currentlyAppliedFilters only carries filter chips, not time ranges or sort intent).
            dispatch(setLastAppliedRecommendation({
                leg: selectedJourneyLeg,
                preferences: payload.preferences ?? null,
                sortPreference: payload.sortPreference ?? null
            }))
            dispatch(setLastSearchedInput({ leg: selectedJourneyLeg, value: naturalLanguageInput }))
        },
        [applySmartSortForCurrentLeg, applySuggestedFilters, dispatch, selectedJourneyLeg]
    )


    //auto search
    useEffect(() => {
        if (!srpResultsError && !srpResultsLoading && !invalidUrl && autoSearchWithQueryParam && srpSmartFilterSort) {

            if (searchWithAI === 'true') {
                dispatch(
                    setSRPFloatingAIChatEnabled(true)
                )
                // AI opens by default on SRP only when the user arrives with `search_with_ai=true`,
                // so this is the one place we emit an AI Air_PageLoad (once per mount).
                if (!aiPageLoadSentRef.current) {
                    aiPageLoadSentRef.current = true
                    triggerSrpAIEvent(AI_EVENT_NAME.PAGE_LOAD, {
                        action_name: AI_ACTION.SRP_PAGE_LOAD,
                        action_type: 'page_load',
                        ai_entry_source: 'home_ai',
                        ai_filter_query: aiFilterQuery || 'none'
                    })
                }
            }
            if (aiFilterQuery) {
                applySmartSortForCurrentLeg()
                applySmartAiFilters(aiFilterQuery)
                dispatch(setTextInput({ leg: selectedJourneyLeg, value: aiFilterQuery }))
            } else {
                dispatch(resetAllAIState())
            }


        }


    }, [query, srpResultsError, srpResultsLoading, invalidUrl, autoSearchWithQueryParam, srpSmartFilterSort, searchWithAI])

    return { applySmartAiFilters, applySmartAiPayload }
}
